import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1] as const;

const layers = [
  { key: "workflows", label: "AI Workflows", sub: "Commercial · In-house · Agentic" },
  { key: "infrastructure", label: "AI Infrastructure", sub: "Build · Deploy · Govern" },
  { key: "systems", label: "Clinical Systems", sub: "Text · Imaging · Data" },
];

const VIOLET = "#6a5cff";
const LINE = "rgba(255,255,255,0.18)";
const LINE_SOFT = "rgba(255,255,255,0.08)";
const INK = "#f4f4f8";
const MUTED = "#8a8ba0";
const DIM = "#5a5b72";

const CYCLE_MS = 2400;
const ROUTE_MS = 6000;

// Abstract anchor points on the SVG overlay (viewBox 200x300).
// Approximates bottom -> middle -> top plane positions in screen space.
const ROUTES: Array<{ b: [number, number]; m: [number, number]; t: [number, number] }> = [
  { b: [62, 232], m: [72, 162], t: [82, 92] },
  { b: [100, 222], m: [100, 152], t: [100, 82] },
  { b: [138, 212], m: [128, 142], t: [118, 72] },
  { b: [62, 232], m: [100, 152], t: [118, 72] },
];

const STATIC_TRACES: Array<{ b: [number, number]; m: [number, number]; t: [number, number] }> = [
  ROUTES[0],
  ROUTES[1],
  ROUTES[2],
  ROUTES[3],
  { b: [138, 212], m: [100, 152], t: [82, 92] },
  { b: [100, 222], m: [72, 162], t: [118, 72] },
];

const toPath = (r: { b: [number, number]; m: [number, number]; t: [number, number] }) =>
  `M ${r.b[0]} ${r.b[1]} L ${r.m[0]} ${r.m[1]} L ${r.t[0]} ${r.t[1]}`;

const LayeredStackDiagram = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.3 });
  const reduce = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [routeIndex, setRouteIndex] = useState(0);

  useEffect(() => {
    if (!inView || reduce) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % layers.length);
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [inView, reduce]);

  useEffect(() => {
    if (!inView || reduce) return;
    const id = setInterval(() => {
      setRouteIndex((i) => (i + 1) % ROUTES.length);
    }, ROUTE_MS);
    return () => clearInterval(id);
  }, [inView, reduce]);

  const activeRoute = ROUTES[routeIndex];

  return (
    <div
      ref={ref}
      className="relative grid grid-cols-1 xl:[grid-template-columns:minmax(360px,1fr)_220px] gap-6 xl:gap-4 items-center"
    >
      {/* Stack column */}
      <div
        className="relative w-full flex justify-center"
        style={{ perspective: "1500px", perspectiveOrigin: "50% 50%" }}
      >
        <div className="relative w-full flex items-center justify-center min-h-[300px] md:min-h-[360px] xl:min-h-[420px] xl:translate-x-[-20px] xl:translate-y-[20px]">
          {/* Signal paths overlay */}
          <svg
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox="0 0 200 300"
            preserveAspectRatio="none"
          >
            {STATIC_TRACES.map((r, idx) => (
              <path
                key={`trace-${idx}`}
                d={toPath(r)}
                fill="none"
                stroke={VIOLET}
                strokeOpacity={0.1}
                strokeWidth={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="2 4"
              />
            ))}
            {!reduce && inView && (
              <motion.path
                key={`active-${routeIndex}`}
                d={toPath(activeRoute)}
                fill="none"
                stroke={VIOLET}
                strokeWidth={1.25}
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: [0, 0.6, 0.6, 0] }}
                transition={{ duration: 5, ease: "easeInOut", times: [0, 0.3, 0.8, 1] }}
                style={{ filter: `drop-shadow(0 0 4px ${VIOLET}80)` }}
                className="hidden md:block"
              />
            )}
          </svg>

          <div
            className="relative w-[min(320px,76vw)] md:w-[360px] xl:w-[340px]"
            style={{
              aspectRatio: "280 / 200",
              transformStyle: "preserve-3d",
              transform: "rotateX(55deg) rotateZ(-18deg)",
            }}
          >
            {layers.map((l, i) => {
              const z = (layers.length - 1 - i) * 80 - ((layers.length - 1) * 80) / 2;
              const isActive = i === activeIndex;
              return (
                <motion.div
                  key={l.key}
                  className="absolute inset-0 rounded-[3px]"
                  initial={{ opacity: 0, z: 0 }}
                  animate={
                    inView
                      ? {
                          opacity: 1,
                          z,
                          backgroundColor: isActive ? "#1a1740" : "#181a33",
                          borderColor: isActive ? VIOLET : LINE,
                          boxShadow: isActive
                            ? `0 0 0 1px ${VIOLET}40, 0 12px 36px -10px ${VIOLET}80`
                            : "0 8px 24px -12px rgba(0,0,0,0.6)",
                        }
                      : {}
                  }
                  transition={{
                    z: {
                      duration: reduce ? 0.6 : 0.9,
                      ease,
                      delay: 0.15 + (layers.length - 1 - i) * 0.14,
                    },
                    opacity: {
                      duration: reduce ? 0.6 : 0.9,
                      ease,
                      delay: 0.15 + (layers.length - 1 - i) * 0.14,
                    },
                    backgroundColor: { duration: 0.6, ease },
                    borderColor: { duration: 0.6, ease },
                    boxShadow: { duration: 0.6, ease },
                  }}
                  style={{
                    transformStyle: "preserve-3d",
                    backgroundImage: `linear-gradient(${LINE_SOFT} 1px, transparent 1px), linear-gradient(90deg, ${LINE_SOFT} 1px, transparent 1px)`,
                    backgroundSize: "20px 20px",
                    borderWidth: 1,
                    borderStyle: "solid",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Labels column */}
      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 xl:mt-0 xl:flex xl:flex-col xl:justify-between xl:h-full xl:py-6 xl:min-h-[360px] m-0 list-none">
        {layers.map((l, i) => {
          const isActive = i === activeIndex;
          return (
            <motion.li
              key={l.key}
              initial={{ opacity: 0, x: 8 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, ease, delay: 0.4 + i * 0.18 }}
              className="flex items-start gap-3"
            >
              <motion.span
                aria-hidden
                className="mt-[6px] inline-block h-2 w-2 rounded-full shrink-0"
                animate={{
                  backgroundColor: isActive ? VIOLET : DIM,
                  boxShadow: isActive ? `0 0 0 3px ${VIOLET}33` : "0 0 0 0 transparent",
                }}
                transition={{ duration: 0.6, ease }}
              />
              <div>
                <motion.div
                  className="text-[12px] tracking-[0.16em] uppercase"
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontWeight: 600,
                  }}
                  animate={{ color: isActive ? VIOLET : INK }}
                  transition={{ duration: 0.6, ease }}
                >
                  {l.label}
                </motion.div>
                <div
                  className="mt-1.5 text-[11px] tracking-[0.14em] uppercase leading-relaxed"
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                    color: MUTED,
                  }}
                >
                  {l.sub}
                </div>
              </div>
            </motion.li>
          );
        })}
      </ul>

      {/* Governance badge */}
      <motion.div
        className="relative mt-8 xl:mt-4 xl:col-span-2 flex justify-center"
        initial={{ opacity: 0, y: 8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, ease, delay: 0.8 }}
      >
        <div
          className="inline-flex flex-col rounded-lg border px-3 py-2"
          style={{
            borderColor: "rgba(255,255,255,0.10)",
            backgroundColor: "rgba(255,255,255,0.03)",
            boxShadow: `0 0 24px ${VIOLET}1f`,
          }}
        >
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
              style={{
                backgroundColor: VIOLET,
                boxShadow: `0 0 8px ${VIOLET}cc`,
              }}
            />
            <span
              className="text-[10px] uppercase tracking-[0.16em]"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                color: INK,
              }}
            >
              Governed by design
            </span>
          </div>
          <div
            className="mt-1 pl-3.5 text-[10px] tracking-[0.12em]"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
              color: MUTED,
            }}
          >
            Evidence · Audit · Oversight
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LayeredStackDiagram;
